// Shared helpers across the admin sub-pages.

export const STAGES = ["Plenary", "Concurrent 1", "Concurrent 2", "Concurrent 3", "Exhibition Hall"];

// Group sessions by (date, start_time, stage, track) so the admin list mirrors
// the public Programme. Sessions with no track (e.g. breaks) become single-item
// groups with no heading. Within each block, items are explicitly sorted by
// `sort_order` (id as stable tiebreaker) so admin reorder arrows always win.
export function groupSessionsByBlock(rows) {
  const sorted = [...rows].sort((a, b) =>
    `${a.date} ${a.start_time} ${a.stage}`.localeCompare(`${b.date} ${b.start_time} ${b.stage}`)
  );
  const map = new Map();
  for (const s of sorted) {
    const trackKey = (s.track || "").trim();
    const key = `${s.date}|${s.start_time}|${s.stage}|${trackKey}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        date: s.date,
        start_time: s.start_time,
        stage: s.stage,
        stage_label: s.stage_label,
        track: trackKey && trackKey.toLowerCase() !== "break" ? trackKey : "",
        items: [],
      });
    }
    map.get(key).items.push(s);
  }
  for (const block of map.values()) {
    block.items.sort((a, b) => {
      const ao = a.sort_order ?? 0;
      const bo = b.sort_order ?? 0;
      if (ao !== bo) return ao - bo;
      return (a.id || "").localeCompare(b.id || "");
    });
  }
  return [...map.values()];
}

// One representative session per block - used by Q&A and Polls admin dropdowns
// where we want one row per block (since both features are block-scoped).
// Drops break-tracked sessions and de-dupes by (date, start_time, stage, track).
export function pickFirstSessionPerBlock(sessions) {
  const seen = new Map();
  [...sessions]
    .sort((a, b) =>
      `${a.date} ${a.start_time} ${a.stage}`.localeCompare(`${b.date} ${b.start_time} ${b.stage}`)
    )
    .forEach((s) => {
      if ((s.track || "").toLowerCase() === "break") return;
      const key = `${s.date}|${s.start_time}|${s.stage}|${(s.track || "").trim()}`;
      if (!seen.has(key)) seen.set(key, s);
    });
  return [...seen.values()];
}

// UK-style date formatting: "2026-06-22" → "22nd June 2026" (long) / "22 Jun" (short).
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function ordinal(n) {
  if (n >= 11 && n <= 13) return `${n}th`;
  const last = n % 10;
  return `${n}${last === 1 ? "st" : last === 2 ? "nd" : last === 3 ? "rd" : "th"}`;
}
export function formatDateLongUK(iso) {
  if (!iso || iso.length < 10) return iso || "";
  const [y, m, d] = iso.slice(0, 10).split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return iso;
  return `${ordinal(d)} ${MONTHS_LONG[m - 1]} ${y}`;
}
export function formatDateShortUK(iso) {
  if (!iso || iso.length < 10) return iso || "";
  const [, m, d] = iso.slice(0, 10).split("-").map((x) => parseInt(x, 10));
  if (!m || !d) return iso;
  return `${d} ${MONTHS_SHORT[m - 1]}`;
}

